// src/pages/Register.js
import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { createUserWithEmailAndPassword } from 'firebase/auth';
import { doc, setDoc, getDoc, getDocs, addDoc, collection, query, where, serverTimestamp } from 'firebase/firestore';
import { auth, db } from '../firebase/config';

export default function Register() {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    password: '',
    confirmPassword: '',
    accessCode: '',
  });

  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();

  const handleChange = (e) => {
    setFormData(prev => ({
      ...prev,
      [e.target.name]: e.target.value,
    }));
  };

  const validateAccessCode = async (code) => {
    const configDoc = await getDoc(doc(db, 'config', 'registration'));

    if (!configDoc.exists()) {
      throw new Error('Registration is not currently available. Please contact your admin.');
    }

    const config = configDoc.data();

    if (code.toUpperCase() !== config.currentCode.toUpperCase()) {
      throw new Error('Invalid access code. Please check with your coordinator.');
    }

    const expiresAt = config.expiresAt?.toDate
      ? config.expiresAt.toDate()
      : new Date(config.expiresAt);

    if (new Date() > expiresAt) {
      throw new Error('This access code has expired. Please contact your coordinator for the current code.');
    }

    return config.currentCode;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    if (formData.password !== formData.confirmPassword) {
      setError('Passwords do not match.');
      return;
    }

    if (formData.password.length < 6) {
      setError('Password must be at least 6 characters.');
      return;
    }

    if (!formData.accessCode.trim()) {
      setError('Please enter your access code.');
      return;
    }

    setLoading(true);

    try {
      const validCode = await validateAccessCode(formData.accessCode.trim());

      const userCredential = await createUserWithEmailAndPassword(
        auth,
        formData.email,
        formData.password
      );

      await setDoc(doc(db, 'users', userCredential.user.uid), {
        id: userCredential.user.uid,
        name: formData.name,
        email: formData.email,
        role: 'learner',
        status: 'active',
        registrationCode: validCode,
        createdAt: serverTimestamp(),
      });

      // Auto-enroll new user in Level 1 catalog
      const level1CatalogSnap = await getDocs(
        query(collection(db, 'catalogs'), where('levelId', '==', 'level-1'))
      );
      if (!level1CatalogSnap.empty) {
        const catalogId = level1CatalogSnap.docs[0].id;
        await addDoc(collection(db, 'userCatalogs'), {
          userId: userCredential.user.uid,
          catalogId: catalogId,
          enrolledAt: new Date().toISOString(),
          enrolledBy: 'registration',
        });
      }

      navigate('/learner');
    } catch (err) {
      console.error('Registration error:', err);

      if (err.code === 'auth/email-already-in-use') {
        setError('This email is already registered. Please sign in instead.');
      } else if (err.code === 'auth/invalid-email') {
        setError('Invalid email address.');
      } else if (err.code === 'auth/weak-password') {
        setError('Password is too weak.');
      } else if (err.message) {
        setError(err.message);
      } else {
        setError('Failed to create account. Please try again.');
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div style={{
      minHeight: '100vh',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      padding: '1rem',
      backgroundColor: 'var(--color-gray-50, #f9fafb)',
    }}>
      <div style={{ width: '100%', maxWidth: '480px' }}>

        {/* Header */}
        <div style={{ textAlign: 'center', marginBottom: '2rem' }}>
          <img
            src="/logo.png"
            alt="Litvill Tests"
            style={{ width: '180px', marginBottom: '1rem' }}
            onError={(e) => { e.target.style.display = 'none'; }}
          />
          <h1 style={{
            color: 'var(--color-teal, #0d9488)',
            marginBottom: '0.5rem',
            fontSize: '1.75rem',
            fontWeight: 700,
          }}>
            Join Litvill Tests
          </h1>
          <p style={{ color: 'var(--color-gray-600, #4b5563)', margin: 0 }}>
            Create your learner account
          </p>
        </div>

        {/* Form Card */}
        <div className="card" style={{ padding: '2rem' }}>
          {error && (
            <div style={{
              padding: '0.75rem 1rem',
              marginBottom: '1.25rem',
              backgroundColor: '#fef2f2',
              color: '#b91c1c',
              borderRadius: 'var(--radius-md, 8px)',
              fontSize: '0.875rem',
              border: '1px solid #fecaca',
            }}>
              {error}
            </div>
          )}

          <form onSubmit={handleSubmit}>
            <Field
              label="Full Name"
              name="name"
              type="text"
              value={formData.name}
              onChange={handleChange}
              placeholder="Enter your full name"
              disabled={loading}
              required
            />
            <Field
              label="Email"
              name="email"
              type="email"
              value={formData.email}
              onChange={handleChange}
              placeholder="your.email@example.com"
              disabled={loading}
              required
            />
            <Field
              label="Password"
              name="password"
              type="password"
              value={formData.password}
              onChange={handleChange}
              placeholder="At least 6 characters"
              disabled={loading}
              required
            />
            <Field
              label="Confirm Password"
              name="confirmPassword"
              type="password"
              value={formData.confirmPassword}
              onChange={handleChange}
              placeholder="Re-enter your password"
              disabled={loading}
              required
            />
            <Field
              label="Access Code"
              name="accessCode"
              type="text"
              value={formData.accessCode}
              onChange={handleChange}
              placeholder="e.g. LIT12"
              disabled={loading}
              required
            />
            <p style={{
              fontSize: '0.75rem',
              color: 'var(--color-gray-500, #6b7280)',
              marginTop: '-0.75rem',
              marginBottom: '1.25rem',
            }}>
              Get your access code from your coordinator. Codes change monthly.
            </p>

            <button
              type="submit"
              disabled={loading}
              style={{
                width: '100%',
                marginTop: '0.25rem',
                padding: '0.75rem',
                backgroundColor: loading ? '#7ec8c6' : '#19a4a0',
                color: '#fff',
                border: 'none',
                borderRadius: '6px',
                fontSize: '1rem',
                fontWeight: 600,
                cursor: loading ? 'not-allowed' : 'pointer',
                transition: 'background-color 0.2s',
              }}
            >
              {loading ? 'Creating Account…' : 'Create Account'}
            </button>
          </form>

          <div style={{
            marginTop: '1.5rem',
            textAlign: 'center',
            fontSize: '0.875rem',
            color: 'var(--color-gray-600, #4b5563)',
          }}>
            Already have an account?{' '}
            <Link
              to="/login"
              style={{
                color: 'var(--color-teal, #0d9488)',
                fontWeight: 500,
                textDecoration: 'none',
              }}
            >
              Sign in
            </Link>
          </div>
        </div>

      </div>
    </div>
  );
}

function Field({ label, name, type, value, onChange, placeholder, disabled, required }) {
  return (
    <div style={{ marginBottom: '1rem' }}>
      <label
        htmlFor={name}
        style={{
          display: 'block',
          fontSize: '0.875rem',
          fontWeight: 500,
          marginBottom: '0.375rem',
          color: 'var(--color-gray-700, #374151)',
        }}
      >
        {label}
      </label>
      <input
        id={name}
        name={name}
        type={type}
        value={value}
        onChange={onChange}
        placeholder={placeholder}
        disabled={disabled}
        required={required}
        style={{
          width: '100%',
          padding: '0.5rem 0.75rem',
          fontSize: '0.875rem',
          border: '1px solid var(--color-gray-300, #d1d5db)',
          borderRadius: 'var(--radius-md, 8px)',
          backgroundColor: disabled ? 'var(--color-gray-100, #f3f4f6)' : 'white',
          boxSizing: 'border-box',
          outline: 'none',
        }}
      />
    </div>
  );
}